import * as React from 'react';
import { encodeSvg } from '../../icons/Icons';
import { generateId, isElementOverflownX } from '../../utils/DomUtil';
import { ElemAttrs, ImgAttrs } from '../../utils/HtmlAttrs';
import { Overlay, OverlayProps } from '../../utils/Overlay';
import { mergeEventHandlers } from '../../utils/ReactHelpers';
// tslint:disable-next-line:max-line-length
import { adjustClientRectForOffset, AggregateTooltipPadding, ContentWrapperStyle, DefaultMaxWidth, TooltipPosition, TooltipPositionInfo, TooltipPositionInfoMap } from './TooltipConsts';

export { TooltipPosition };

export interface TooltipProps extends Pick<OverlayProps, 'stackingRootId' | 'zIndex'> {
  /**
   * The trigger element for the tooltip. Must accept DOM attributes. If not provided, a default 18x18 "info" icon will be used instead.
   * For the simple case of plain text, wrap the text in a `<span>`.
   */
  children?: React.ReactElement<Pick<ElemAttrs,
    'aria-describedby' | 'onBlur' | 'onClick' | 'onFocus' | 'onKeyUp' | 'onMouseEnter' | 'onMouseLeave' | 'onMouseMove' | 'tabIndex'
  >>;

  /** Set to true to render the tooltip against the cursor rather than the rendered children. */
  followCursor?: boolean;

  /**
   * The function that is called to get the tooltip content.
   * The callback must return a `ReactElement`, i.e. it cannot return a plain string.
   */
  getContent?: () => Promise<JSX.Element> | JSX.Element | null;

  /**
   * The desired max width of the tooltip container.
   * @default 300
   */
  maxWidth?: number;

  /**
   * When true, tooltip will only display if the wrapped element's `clientWidth < scrollWidth`,
   * i.e. when it is horizontally overflowing.
   */
  onlyShowWhenClipped?: boolean;

  /**
   * The desired position that the tooltip will be displayed against the target.
   * NOTE: If there is not enough room for the tooltip at the desired position, we will attempt the opposite position.
   * @default TooltipPosition.Right
   */
  position?: TooltipPosition;

  /** Test id to be attached to the tooltip. */
  testId?: string;
}

export interface TooltipState {
  /** The ClientRect that the tooltip overlay should anchor against. */
  anchorClientRect: ClientRect | undefined;

  /** The content to be displayed. */
  content: React.ReactNode;

  /** The width of the tooltip. */
  width: number | undefined;
}

function isFocusEvent(e: React.SyntheticEvent): e is React.FocusEvent {
  return e.type === 'focus';
}

// tslint:disable-next-line:no-any
function isPromiseLike<T>(o: any): o is Promise<T> {
  return 'then' in o && typeof o.then === 'function';
}

export const InfoIcon: React.FC<ImgAttrs> = (props) => <img {...props} draggable={false} src={encodeSvg(`<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path fill="#666" fill-rule="evenodd" d="M8.5 1C12.6 1 16 4.4 16 8.5S12.6 16 8.5 16 1 12.6 1 8.5 4.4 1 8.5 1zm0 1C4.9 2 2 4.9 2 8.5S4.9 15 8.5 15 15 12.1 15 8.5 12.1 2 8.5 2zM9 7v5H8V7h1zm0-2v1H8V5h1z"/></svg>`)} />;
const DefaultTriggerElement = <InfoIcon />;

/**
 * Wrapper which displays a popup when the child element receives focus or is hovered.
 *
 * **NOTE**: this component is for static tooltip content only. It is not WCAG-compliant to put interactive content
 * inside the tooltip.
 *
 * @example
 *  <Tooltip getContent={() => <span>Some interesting info</span>}>
 *    <span>Hover here for more info</span>
 *  </Tooltip>
 */
export class Tooltip extends React.Component<TooltipProps, TooltipState> {
  private readonly tooltipId: string = generateId();
  private tooltipContentElement: HTMLElement | null = null;

  public static defaultProps: Pick<TooltipProps, 'children' | 'maxWidth' | 'position'> = {
    children: DefaultTriggerElement,
    maxWidth: DefaultMaxWidth,
    position: TooltipPosition.Right,
  };

  private static displayedTooltip?: { dismiss: () => void; tooltipId: string; };

  private static dismissDisplayedTooltip(): void {
    if (Tooltip.displayedTooltip) {
      const { dismiss } = Tooltip.displayedTooltip;
      Tooltip.displayedTooltip = undefined;

      try {
        dismiss();
      } catch (error) {
        console.warn({ message: 'failed to dismiss displayed tooltip', error });
      }
    }
  }

  public state: TooltipState = {
    anchorClientRect: undefined,
    content: undefined,
    width: undefined,
  };

  public componentDidUpdate(prevProps: TooltipProps, prevState: TooltipState): void {
    if (this.state.content && prevProps.getContent !== this.props.getContent) {
      this.refreshContent();
    }
  }

  public componentWillUnmount(): void {
    this.dismissTooltipOverlay();
  }

  private fixWidth(): void {
    if (this.state.content && this.state.anchorClientRect && this.tooltipContentElement && this.state.width === undefined) {
      this.setState({ width: this.tooltipContentElement.getBoundingClientRect().width + AggregateTooltipPadding });
    }
  }

  private handleTooltipOverlayContentRef = (element: HTMLElement | null): void => {
    this.tooltipContentElement = element ? element.firstElementChild as HTMLElement : null;
  }

  private handleFocus: React.FocusEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.showTooltipOverlay(e);
  }, e => this.props.children?.props.onFocus?.(e));

  private handleBlur: React.FocusEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.dismissTooltipOverlay();
  }, e => this.props.children?.props.onBlur?.(e));

  private handleKeyUp: React.KeyboardEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (this.state.content && this.state.anchorClientRect && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.dismissTooltipOverlay();
    }
  }, e => this.props.children?.props.onKeyUp?.(e));

  private handleMouseEnter: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.showTooltipOverlay(e);
  }, e => this.props.children?.props.onMouseEnter?.(e));

  private handleMouseMove: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (this.state.content && this.state.anchorClientRect && !this.props.followCursor) { return; }
    this.showTooltipOverlay(e);
  }, e => this.props.children?.props.onMouseMove?.(e));

  private handleMouseLeave: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.dismissTooltipOverlay();
  }, e => this.props.children?.props.onMouseLeave?.(e));

  private showTooltipOverlay = (e: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>): void => {
    if (this.props.onlyShowWhenClipped && !isElementOverflownX(e.currentTarget)) {
      return;
    }

    // this is done unconditionally to support the `followCursor` behavior
    this.setState({ anchorClientRect: this.getAnchorClientRectFromEvent(e) });

    if (this.state.content) { return; }

    if (Tooltip.displayedTooltip && Tooltip.displayedTooltip.tooltipId !== this.tooltipId) {
      Tooltip.dismissDisplayedTooltip();
    }
    Tooltip.displayedTooltip = { dismiss: this.dismissTooltipOverlay, tooltipId: this.tooltipId };
    this.refreshContent();
  }

  private refreshContent(): void {
    const setContent = (content: React.ReactElement) => {
      this.setState({ content, width: undefined }, this.fixWidth);
    };

    const contentyThing = this.props.getContent?.();
    if (!contentyThing) {
      this.dismissTooltipOverlay();
    } else if (isPromiseLike(contentyThing)) {
      contentyThing
        .then(setContent)
        .catch(error => {
          console.warn({ message: `'getContent' promise rejected`, error });
          this.dismissTooltipOverlay();
        });
    } else {
      setContent(contentyThing);
    }
  }

  private dismissTooltipOverlay = (): void => {
    if (Tooltip.displayedTooltip && this.tooltipId === Tooltip.displayedTooltip.tooltipId) {
      Tooltip.displayedTooltip = undefined;
    }

    this.setState({ anchorClientRect: undefined, content: undefined, width: undefined });
  }

  private getAnchorClientRectFromEvent(e: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>): ClientRect | undefined {
    if (isFocusEvent(e) && this.state.anchorClientRect) {
      return this.state.anchorClientRect;
    } else if (isFocusEvent(e) || !this.props.followCursor) {
      return adjustClientRectForOffset(e.currentTarget.getBoundingClientRect());
    } else {
      return adjustClientRectForOffset({
        bottom: e.clientY,
        height: 0,
        left: e.clientX,
        right: e.clientX,
        top: e.clientY,
        width: 0,
      });
    }
  }

  private getAnchorClientRect = (): ClientRect | undefined => this.state.anchorClientRect;

  private renderChildren(): React.ReactNode {
    return (
      React.cloneElement(React.Children.only(this.props.children!), {
        'aria-describedby': this.state.anchorClientRect ? this.tooltipId : undefined,
        onBlur: this.handleBlur,
        onFocus: this.handleFocus,
        onKeyUp: this.handleKeyUp,
        onMouseEnter: this.handleMouseEnter,
        onMouseLeave: this.handleMouseLeave,
        onMouseMove: this.handleMouseMove,
        tabIndex: this.props.children?.props.tabIndex ?? 0,
      })
    );
  }

  private renderTooltipOverlay(): React.ReactNode {
    if (!this.state.content || !this.state.anchorClientRect) { return undefined; }

    const tooltipSideProps: TooltipPositionInfo = TooltipPositionInfoMap[this.props.position!];

    /**
     * In order to have the content wrapper grow to fit the tooltip contents, we initially render the wrapper with a width of maxWidth.
     * Once the tooltip content is rendered, we get a reference to the content element and get the actual rendered width. This width is
     * then used, along with the left/right padding and border widths to calculate the width of the content wrapper. This results in a
     * content wrapper that fits to the tooltip content.
     */

    const contentWrapperStyle: React.CSSProperties = {
      ...ContentWrapperStyle,
      maxWidth: this.props.maxWidth,
      overflow: 'hidden',
      visibility: this.state.width === undefined ? 'hidden' : 'visible',
      width: Math.ceil(this.state.width || this.props.maxWidth!), // TFS 826030 round up to nearest int to avoid layout errors in Safari
    };

    return (
      <Overlay
        allowAutoPosition
        anchorOrigin={tooltipSideProps.anchorOrigin}
        disablePointerEvents={true}
        getAnchorClientRect={this.getAnchorClientRect}
        shouldAutoPositionOnScrollingRootScroll={this.props.followCursor}
        stackedOrigin={tooltipSideProps.stackedOrigin}
        // tslint:disable-next-line:deprecation
        stackingRootId={this.props.stackingRootId}
        zIndex={this.props.zIndex}
      >
        <div
          data-tb-test-id={this.props.testId}
          id={this.tooltipId}
          ref={this.handleTooltipOverlayContentRef}
          role='tooltip'
          style={contentWrapperStyle}
        >
          {this.state.content}
        </div>
      </Overlay>
    );
  }

  public render(): React.ReactNode {
    return <>
      {this.renderChildren()}
      {this.renderTooltipOverlay()}
    </>;
  }
}
